/**
 * 会員登録: 入力 → 確認（静的ビルド用 sessionStorage）
 * 入力 SP 4098-18911 / PC 4098-19831 / 確認 PC 4098-19904・SP 4098-18973
 */
import { validateMemberRegisterForm, initMemberRegisterFormValidation } from './member-register-form-validation.js';
import { isRangePlaceholderValue, resyncRangeSelectControls } from './range-select-control.js';

const STORAGE_KEY = 'takamatsu_member_register_v1';
const CONFIRM_PATH = '/member_register_confirm.html';
const COMPLETE_PATH = '/member_register_complete.html';

/** TODO: デザイン確認用。本番では false にし、データなし時は empty を表示 */
const MEMBER_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

const PREF_TYPE_LABELS = {
	new_house: '新築一戸建て',
	used_house: '中古一戸建て',
	mansion: '中古マンション',
	land: '土地',
};

const PREF_AREA_LABELS = {
	bunkyo: '文京区',
	shinjuku: '新宿区',
	shibuya: '渋谷区',
	meguro: '目黒区',
	shinagawa: '品川区',
	minato: '港区',
	chuo: '中央区',
	chiyoda: '千代田区',
	ota: '大田区',
	setagaya: '世田谷区',
	koto: '江東区',
	taito: '台東区',
	sumida: '墨田区',
	nakano: '中野区',
	suginami: '杉並区',
	kita: '北区',
	toshima: '豊島区',
	katsushika: '葛飾区',
	adachi: '足立区',
	edogawa: '江戸川区',
	arakawa: '荒川区',
	nerima: '練馬区',
	itabashi: '板橋区',
	other: 'その他',
};

const BUILDING_AGE_LABELS = {
	any: '指定なし',
	within_5y: '5年以内',
	within_10y: '10年以内',
	within_20y: '20年以内',
};

const STATION_WALK_LABELS = {
	any: '指定なし',
	within_10m: '10分以内',
	within_20m: '20分以内',
};

function getForm() {
	return document.querySelector('form[data-member-register-flow]#member-register-form');
}

function collectPrefTypeDisplay(form) {
	const checks = form.querySelectorAll('input[name="member_pref_type[]"]:checked');
	const labels = [...checks].map((cb) => PREF_TYPE_LABELS[cb.value] || '').filter(Boolean);
	return labels.length ? labels.join('、') : '—';
}

function collectPrefAreaDisplay(form) {
	const fd = new FormData(form);
	const checks = form.querySelectorAll('input[name="member_pref_area[]"]:checked');
	const labels = [...checks].map((cb) => PREF_AREA_LABELS[cb.value] || '').filter(Boolean);
	const text = (fd.get('member_pref_area_text') || '').toString().trim();
	const parts = [...labels];
	if (text) parts.push(text);
	return parts.length ? parts.join('、') : '—';
}

function getSelectLabel(selectEl) {
	if (!selectEl || selectEl.selectedIndex < 0) return '';
	return selectEl.options[selectEl.selectedIndex].text.trim();
}

function collectPriceDisplay(form) {
	const minEl = form.querySelector('#member-price-min');
	const maxEl = form.querySelector('#member-price-max');
	const minV = minEl ? minEl.value : '';
	const maxV = maxEl ? maxEl.value : '';
	if (isRangePlaceholderValue(minV) && isRangePlaceholderValue(maxV)) return '—';
	const minT = minEl && !isRangePlaceholderValue(minV) ? getSelectLabel(minEl) : '';
	const maxT = maxEl && !isRangePlaceholderValue(maxV) ? getSelectLabel(maxEl) : '';
	if (!minT && !maxT) return '—';
	if (minT && maxT) return `${minT} 〜 ${maxT}`;
	return minT || maxT;
}

function collectBuildingAgeDisplay(form) {
	const fd = new FormData(form);
	const v = (fd.get('member_building_age') || '').toString();
	return BUILDING_AGE_LABELS[v] || '—';
}

function collectStationWalkDisplay(form) {
	const fd = new FormData(form);
	const v = (fd.get('member_station_walk') || '').toString();
	return STATION_WALK_LABELS[v] || '—';
}

/** 確認画面用とは別に、「修正する」で戻ったとき用の生フィールド（visit-flow の collectRawFormFields と同役） */
function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	const checks = form.querySelectorAll('input[name="member_pref_type[]"]:checked');
	const types = [...checks].map((cb) => cb.value);
	const areaChecks = form.querySelectorAll('input[name="member_pref_area[]"]:checked');
	const areas = [...areaChecks].map((cb) => cb.value);
	return {
		member_name_family: pick('member_name_family').trim(),
		member_name_given: pick('member_name_given').trim(),
		member_tel: pick('member_tel').trim(),
		member_email: pick('member_email').trim(),
		member_password: pick('member_password'),
		member_password_confirm: pick('member_password_confirm'),
		member_privacy: fd.get('member_privacy') === '1',
		member_pref_type: types,
		member_pref_area: areas,
		member_pref_area_text: pick('member_pref_area_text').trim(),
		member_price_min: pick('member_price_min'),
		member_price_max: pick('member_price_max'),
		member_building_age: pick('member_building_age'),
		member_station_walk: pick('member_station_walk'),
		member_pref_notes: pick('member_pref_notes'),
	};
}

function collectPayload(form) {
	const fd = new FormData(form);
	const family = (fd.get('member_name_family') || '').toString().trim();
	const given = (fd.get('member_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';
	return {
		member_name_full: nameFull,
		member_email: (fd.get('member_email') || '').toString().trim() || '—',
		member_tel: (fd.get('member_tel') || '').toString().trim() || '—',
		member_pref_type_display: collectPrefTypeDisplay(form),
		member_pref_area_display: collectPrefAreaDisplay(form),
		member_price_display: collectPriceDisplay(form),
		member_building_age_display: collectBuildingAgeDisplay(form),
		member_station_walk_display: collectStationWalkDisplay(form),
		member_pref_notes: (fd.get('member_pref_notes') || '').toString().trim() || '—',
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	if (Object.prototype.hasOwnProperty.call(raw, 'member_privacy')) {
		const cb = form.querySelector('input[name="member_privacy"]');
		if (cb) cb.checked = Boolean(raw.member_privacy);
	}

	if (Array.isArray(raw.member_pref_type)) {
		form.querySelectorAll('input[name="member_pref_type[]"]').forEach((el) => {
			el.checked = raw.member_pref_type.includes(el.value);
		});
	}

	if (Array.isArray(raw.member_pref_area)) {
		form.querySelectorAll('input[name="member_pref_area[]"]').forEach((el) => {
			el.checked = raw.member_pref_area.includes(el.value);
		});
	}

	const areaTextEl = form.querySelector('[name="member_pref_area_text"]');
	if (areaTextEl) {
		const legacyText = raw.member_pref_area_text ?? raw.member_pref_area_other ?? raw.member_pref_area;
		if (legacyText != null && typeof legacyText === 'string') {
			areaTextEl.value = legacyText;
		}
	}

	for (const radioName of ['member_building_age', 'member_station_walk']) {
		if (raw[radioName] != null && String(raw[radioName]) !== '') {
			const v = String(raw[radioName]);
			form.querySelectorAll(`input[name="${radioName}"]`).forEach((radio) => {
				radio.checked = radio.value === v;
			});
		}
	}

	const skip = new Set([
		'member_privacy',
		'member_pref_type',
		'member_pref_area',
		'member_pref_area_text',
		'member_pref_area_other',
		'member_building_age',
		'member_station_walk',
	]);
	for (const key of Object.keys(raw)) {
		if (skip.has(key)) continue;
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;

		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function setText(el, text) {
	if (!el) return;
	el.textContent = text;
}

function setPreFormattedDesc(el, text) {
	if (!el) return;
	el.replaceChildren();
	const lines = String(text).split('\n');
	lines.forEach((line) => {
		const span = document.createElement('span');
		span.className = 'visit__confirmPreLine';
		span.textContent = line;
		el.appendChild(span);
	});
}

function initFormPage() {
	const form = getForm();
	if (!form) return;

	initMemberRegisterFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
		resyncRangeSelectControls(form);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateMemberRegisterForm(form)) {
			return;
		}
		const payload = collectPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...payload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function initConfirmPage() {
	const page = document.querySelector('[data-member-register-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('member-register-confirm-block');
	const empty = document.getElementById('member-register-confirm-empty');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('member-register-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (MEMBER_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const hasRequired =
		draft.member_name_full !== '—' &&
		draft.member_email !== '—' &&
		draft.member_email.includes('@') &&
		draft.member_pref_area_display !== '—';

	if (!hasRequired) {
		if (MEMBER_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-member-field]').forEach((el) => {
		const key = el.getAttribute('data-member-field');
		if (!key || !(key in draft)) return;
		const out = String(draft[key]);
		if (el.classList.contains('visit__confirmDesc--pre')) {
			setPreFormattedDesc(el, out);
		} else {
			setText(el, out);
		}
	});
}

export function initMemberRegisterFlow() {
	const page = document.querySelector('[data-member-register-page]')?.getAttribute('data-member-register-page');
	if (!page) return;
	if (page === 'form') initFormPage();
	if (page === 'confirm') initConfirmPage();
}
